import { useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Table,
  TableHeader,
  TableColumn,
  TableBody,
  TableRow,
  TableCell,
  Tooltip,
  Chip,
  Spinner,
} from '@heroui/react'
import { HelpCircle, AlertCircle } from 'lucide-react'
import { StatusIndicator } from '../shared/StatusIndicator'
import { FunnelBadge } from '../shared/FunnelBadge'
import { usePricingView } from '../../hooks/use-pricing-view'
import { useFunnelAnalyticsMap } from '../../hooks/use-funnel-analytics-map'
import type { Funnel, FunnelAnalytics, PricingViewEntry, PricingStepEntry } from '../../types'

interface FunnelPricingTableProps {
  funnels: Funnel[]
}

function formatNumber(num: number): string {
  return new Intl.NumberFormat('en-US').format(num)
}

function formatCurrency(num: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(num)
}

function formatDiscount(step: PricingStepEntry): string {
  if (!step.discountType || step.discountValue === undefined) return '-'
  if (step.discountType === 'percentage') return `${step.discountValue}%`
  if (step.discountType === 'fixed') return formatCurrency(step.discountValue)
  return `${step.discountValue} (${step.discountType})`
}

function StepTypeChip({ type }: { type: string }) {
  const colorMap: Record<string, 'primary' | 'secondary' | 'warning'> = {
    upsell: 'primary',
    downsell: 'secondary',
    checkout: 'warning',
  }
  return (
    <Chip size="sm" variant="flat" color={colorMap[type] ?? 'default'}>
      {type}
    </Chip>
  )
}

export function FunnelPricingTable({ funnels }: FunnelPricingTableProps) {
  const navigate = useNavigate()
  const { entries, loading, error } = usePricingView()

  const funnelIds = useMemo(() => funnels.map((f) => f.id), [funnels])
  const { analyticsMap } = useFunnelAnalyticsMap(funnelIds)

  // Merge API pricing data with funnel list
  const pricingMap = useMemo(() => {
    const map = new Map<string, PricingViewEntry>()
    for (const entry of entries) {
      map.set(entry.funnelId, entry)
    }
    return map
  }, [entries])

  // Build rows: one per funnel, with step details if available
  const rows = useMemo(() => {
    return funnels.map((funnel) => {
      const pricing = pricingMap.get(funnel.id)
      const analytics = analyticsMap.get(funnel.id) ?? null
      return { funnel, pricing, analytics }
    })
  }, [funnels, pricingMap, analyticsMap])

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Spinner size="lg" />
        <span className="ml-3 text-sm text-fn-text-muted">
          Loading pricing data...
        </span>
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex items-center gap-2 p-4 rounded-lg bg-danger-50 text-danger-700 text-sm">
        <AlertCircle className="w-4 h-4 flex-shrink-0" />
        <span>Failed to load pricing data: {error}. Showing analytics only.</span>
      </div>
    )
  }

  return (
    <div className="overflow-x-auto">
      <Table
        aria-label="Funnels pricing table"
        classNames={{
          wrapper: 'rounded-lg border border-fn-border min-w-[1100px]',
          th: 'bg-fn-muted text-fn-text-sub font-medium text-xs',
          td: 'text-sm',
        }}
      >
        <TableHeader>
          <TableColumn className="sticky left-0 z-10 bg-fn-muted">NAME</TableColumn>
          <TableColumn>STATUS</TableColumn>
          <TableColumn>TYPE</TableColumn>
          <TableColumn>
            <div className="flex items-center gap-1">
              STEPS
              <Tooltip content="Number of offer steps in this funnel">
                <HelpCircle className="w-3.5 h-3.5 text-default-400 cursor-help" />
              </Tooltip>
            </div>
          </TableColumn>
          <TableColumn>
            <div className="flex items-center gap-1">
              OFFER BREAKDOWN
              <Tooltip content="Step types and discount details for each step">
                <HelpCircle className="w-3.5 h-3.5 text-default-400 cursor-help" />
              </Tooltip>
            </div>
          </TableColumn>
          <TableColumn>
            <div className="flex items-center gap-1">
              IMPRESSIONS
              <Tooltip content="Number of times this funnel was viewed">
                <HelpCircle className="w-3.5 h-3.5 text-default-400 cursor-help" />
              </Tooltip>
            </div>
          </TableColumn>
          <TableColumn>
            <div className="flex items-center gap-1">
              CONV. RATE
              <Tooltip content="Percentage of visitors who completed a purchase">
                <HelpCircle className="w-3.5 h-3.5 text-default-400 cursor-help" />
              </Tooltip>
            </div>
          </TableColumn>
          <TableColumn>
            <div className="flex items-center gap-1">
              REVENUE
              <Tooltip content="Total revenue generated by this funnel">
                <HelpCircle className="w-3.5 h-3.5 text-default-400 cursor-help" />
              </Tooltip>
            </div>
          </TableColumn>
          <TableColumn>
            <div className="flex items-center gap-1">
              AOV
              <Tooltip content="Average Order Value - average spend per transaction">
                <HelpCircle className="w-3.5 h-3.5 text-default-400 cursor-help" />
              </Tooltip>
            </div>
          </TableColumn>
        </TableHeader>
        <TableBody emptyContent="No funnels found.">
          {rows.map(({ funnel, pricing, analytics }: { funnel: Funnel; pricing: PricingViewEntry | undefined; analytics: FunnelAnalytics | null }) => (
            <TableRow
              key={funnel.id}
              className="hover:bg-fn-hover cursor-pointer"
              onDoubleClick={() => navigate(`/funnels/${funnel.id}`)}
            >
              <TableCell className="sticky left-0 z-10 bg-tf-bg-card">
                <div>
                  <div className="font-medium text-fn-text">
                    {funnel.name}
                  </div>
                  {funnel.description && (
                    <div className="text-xs mt-0.5 line-clamp-1 text-fn-text-muted">
                      {funnel.description}
                    </div>
                  )}
                </div>
              </TableCell>
              <TableCell>
                <StatusIndicator isActive={funnel.isActive} />
              </TableCell>
              <TableCell>
                {funnel.funnelType ? (
                  <FunnelBadge type="funnel" value={funnel.funnelType} />
                ) : (
                  <span className="text-fn-text-muted">-</span>
                )}
              </TableCell>
              <TableCell>
                <span className="text-fn-text">
                  {pricing?.stepsCount ?? funnel.steps?.length ?? '-'}
                </span>
              </TableCell>
              <TableCell>
                {pricing && pricing.steps.length > 0 ? (
                  <div className="flex flex-col gap-1 max-w-[280px]">
                    {pricing.steps.map((step) => (
                      <div key={step.stepId} className="flex items-center gap-1.5 text-xs">
                        <StepTypeChip type={step.stepType} />
                        <span className="truncate text-fn-text">
                          {step.offerName || step.stepName}
                        </span>
                        <span className="ml-auto font-medium whitespace-nowrap text-fn-active">
                          {formatDiscount(step)}
                        </span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <span className="text-xs text-fn-text-muted">
                    No steps
                  </span>
                )}
              </TableCell>
              <TableCell>
                <span className="text-fn-text">
                  {analytics ? formatNumber(analytics.impressions) : '-'}
                </span>
              </TableCell>
              <TableCell>
                <span className="text-fn-text">
                  {analytics ? `${analytics.conversionRate.toFixed(2)}%` : '-'}
                </span>
              </TableCell>
              <TableCell>
                <span className="font-medium text-fn-active">
                  {analytics ? formatCurrency(analytics.revenue) : '-'}
                </span>
              </TableCell>
              <TableCell>
                <span className="text-fn-text">
                  {analytics ? formatCurrency(analytics.avgOrderValue) : '-'}
                </span>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  )
}
